import { animate, createDrawable } from "animejs";
import type { JSAnimation } from "animejs";

import type { SelectEffect } from "./Select.types";
import {
  SELECT_ACK_ATTRIBUTE,
  SELECT_CHECK_DURATION_MS,
  SELECT_PULSE_DURATION_MS,
} from "./constants";

const INDICATOR_SELECTOR = '[data-slot="select-item-indicator"]';

export type SelectAcknowledgement = {
  /** Stops the effect without running its completion callback. */
  cancel: () => void;
};

/**
 * Plays the acknowledgement on the chosen item, then calls `onComplete` —
 * which is what actually dismisses the popup.
 */
export type SelectEffectRunner = (
  item: HTMLElement,
  onComplete: () => void,
) => SelectAcknowledgement;

/**
 * An effect that only marks the chosen item and lets CSS do the rest.
 *
 * A timer rather than `animationend` ends it: an animation that never runs —
 * a backgrounded tab, animations disabled — would otherwise leave the popup
 * open for good.
 */
function marker(value: string, durationMs: number): SelectEffectRunner {
  return (item, onComplete) => {
    let settled = false;

    item.setAttribute(SELECT_ACK_ATTRIBUTE, value);

    const timer = window.setTimeout(() => {
      if (settled) {
        return;
      }

      settled = true;
      item.removeAttribute(SELECT_ACK_ATTRIBUTE);
      onComplete();
    }, durationMs);

    return {
      cancel() {
        if (settled) {
          return;
        }

        settled = true;
        window.clearTimeout(timer);
        item.removeAttribute(SELECT_ACK_ATTRIBUTE);
      },
    };
  };
}

/**
 * Draws the item's check mark in, with `Checkbox`'s animation: the same
 * `createDrawable` path and easing, over a longer duration.
 */
const check: SelectEffectRunner = (item, onComplete) => {
  let settled = false;
  let animation: JSAnimation | null = null;

  function finish() {
    if (settled) {
      return;
    }

    settled = true;
    window.clearTimeout(backstop);
    onComplete();
  }

  // A hidden tab suspends `requestAnimationFrame` and throttles `animate`, so
  // neither the frame below nor the draw's own completion is guaranteed to
  // arrive. Without this the popup would stay open until the tab came back.
  const backstop = window.setTimeout(finish, SELECT_CHECK_DURATION_MS * 3);

  // The indicator only mounts once the value is committed, which React has not
  // done yet when this runs. A frame later the node exists and has not been
  // painted, so blanking the path here does not flash a finished check first.
  const frame = requestAnimationFrame(() => {
    const path = item.querySelector<SVGPathElement>(
      `${INDICATOR_SELECTOR} path`,
    );

    if (!path) {
      finish();

      return;
    }

    const [drawable] = createDrawable(path, 1, 1);

    animation = animate(drawable, {
      draw: "0 1",
      duration: SELECT_CHECK_DURATION_MS,
      ease: "outQuad",
      onComplete: finish,
    });
  });

  return {
    cancel() {
      if (settled) {
        return;
      }

      settled = true;
      window.clearTimeout(backstop);
      cancelAnimationFrame(frame);
      animation?.cancel();
    },
  };
};

/**
 * `press` and `none` are absent on purpose: neither holds the popup open, so
 * neither has anything to play once the choice is made.
 */
export const SELECT_EFFECT_RUNNERS: Partial<
  Record<SelectEffect, SelectEffectRunner>
> = {
  pulse: marker("pulse", SELECT_PULSE_DURATION_MS),
  check,
};
